import type { ComponentProps } from 'react';

export function PaginationNavigationButton({
  direction,
  enabled,
  onClick,
  restProps,
}: {
  direction: 'previous' | 'next';
  enabled: boolean;
  onClick: () => void;
  restProps?: ComponentProps<'button'>;
}) {
  const isPrevious = direction === 'previous';
  const title = isPrevious ? 'Previous' : 'Next';
  const path = isPrevious
    ? 'M15.707 5.293a1 1 0 0 1 0 1.414L10.414 12l5.293 5.293a1 1 0 0 1-1.414 1.414l-6-6a1 1 0 0 1 0-1.414l6-6a1 1 0 0 1 1.414 0Z'
    : 'M8.293 5.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L13.586 12 8.293 6.707a1 1 0 0 1 0-1.414Z';

  if (enabled) {
    return (
      <button
        onClick={onClick}
        {...(restProps || {})}
        className="relative inline-flex h-8 w-8 items-center justify-center border border-[--color]/30 bg-brand-white font-medium text-[--color]/80 text-sm hover:bg-[--color]/5"
      >
        <span className="sr-only">{title}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
        >
          <title>{title}</title>
          <path
            fill="currentColor"
            fillRule="evenodd"
            d={path}
            clipRule="evenodd"
          />
        </svg>
      </button>
    );
  }

  return (
    <div className="relative inline-flex h-8 w-8 cursor-not-allowed items-center justify-center border border-[--color]/30 bg-[--color]/5 font-medium text-[--color]/30 text-sm">
      <span className="sr-only">{title}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
      >
        <title>{title}</title>
        <path
          fill="currentColor"
          fillRule="evenodd"
          d={path}
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
}

export function PaginationEllipsisItem() {
  return (
    <span className="relative inline-flex h-8 w-8 items-center justify-center border border-[--color]/30 bg-brand-white font-medium text-[--color]/80 text-sm">
      …
    </span>
  );
}

export function PaginationPageNumberButton({
  pageNumber,
  currentPage,
  onClick,
  restProps,
}: {
  pageNumber: number;
  currentPage: number;
  onClick: () => void;
  restProps?: ComponentProps<'button'>;
}) {
  const isCurrentPage = pageNumber === currentPage;
  return (
    <button
      onClick={onClick}
      {...(restProps || {})}
      className={`relative inline-flex h-8 w-8 items-center justify-center border font-medium text-sm ${
        isCurrentPage
          ? 'border-[--color] bg-[--color] text-white'
          : 'border-[--color]/30 bg-white text-[--color]/80 hover:bg-[--color]/5'
      }`}
    >
      {pageNumber}
    </button>
  );
}

export function usePaginationRange({
  currentPage,
  totalPages,
}: {
  currentPage: number;
  totalPages: number;
}) {
  let pageNumbers: (number | '...')[] = [];

  if (totalPages <= 7) {
    // Show all pages if there are 7 or fewer
    pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  } else {
    // Logic for more than 7 pages
    // biome-ignore lint/style/useCollapsedElseIf: <explanation>
    if (currentPage <= 4) {
      // Near the beginning: show first pages + ellipsis + last page
      // Page 1: [1, 2, 3, ..., 15]
      // Page 2: [1, 2, 3, 4, ..., 15]
      // Page 3: [1, 2, 3, 4, 5, ..., 15]
      // Page 4: [1, 2, 3, 4, 5, 6, ..., 15]
      const endIndex = Math.min(currentPage + 2, 6);
      for (let i = 1; i <= endIndex; i++) {
        pageNumbers.push(i);
      }
      pageNumbers.push('...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      // Near the end: show 1, ..., last few pages
      pageNumbers.push(1, '...');
      const startIndex = Math.max(totalPages - 5, currentPage - 2);
      for (let i = startIndex; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // In the middle: show 1, ..., current-1, current, current+1, ..., last
      pageNumbers.push(
        1,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        totalPages
      );
    }
  }

  return {
    pageNumbers,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  };
}
